import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { RUN_ENVIRONMENT_BASE } from "@via/pages/account/profile/run-environments/data-roots.api.tsx";
import {
  containerRuntimeQueryKey,
  fetchContainerRuntime,
} from "@via/pages/account/profile/run-environments/container-runtime.api.tsx";
import {
  type AppLaunchEndReason,
  type CreateHpcAppLaunchBody,
  CredentialView,
  type GetUserCredentialsResponse,
  type HpcAppLaunchView,
  isLiveAppLaunchState,
  type OpenHpcAppLaunchResponse,
  type ProfileClusterForClient,
  type RunEnvironmentContainerRuntimeResponseBody,
  type StartRuntimeProbeResponseBody,
} from "common";

/**
 * HPC app launches (agent-9-1): the browser side of backend/src/routes/hpcAppLaunch.ts and of the
 * apps blocks of the container runtime routes (backend/src/routes/mountAllowlist.ts). Every route
 * here is browser session only and absent while HPC_APPS_ENABLED is off, so callers gate on the
 * flag before any of these hooks is enabled.
 */
export const HPC_APP_LAUNCHES_BASE = "/api/v1/hpc-app-launches";

const LIVE_POLL_MS = 5000;

// The container runtime hooks moved beside the run environment pages (agent-7-4), because the
// Container execution card reads them too. Re-exported here so the Apps screens keep their imports.
export {
  containerRuntimeQueryKey,
  fetchContainerRuntime,
  useContainerRuntime,
  useSaveContainerRuntime,
} from "@via/pages/account/profile/run-environments/container-runtime.api.tsx";

/** The run environment list of the profile pages (profile.api.tsx). */
const USER_ENVIRONMENTS_PATH = "/api/user/v1/credentials";

export const hpcAppLaunchesQueryKey = ["hpc-app-launches"] as const;

/** Polls every 5 seconds while any launch is live, and stops once every launch has ended. */
export const useHpcAppLaunches = (
  enabled = true
): UseQueryResult<HpcAppLaunchView[], Error> =>
  useQuery<HpcAppLaunchView[], Error>({
    queryKey: hpcAppLaunchesQueryKey,
    queryFn: async () => {
      const res = await axios.get(HPC_APP_LAUNCHES_BASE);
      return res.data as HpcAppLaunchView[];
    },
    enabled,
    retry: false,
    refetchInterval: query =>
      (query.state.data ?? []).some(launch =>
        isLiveAppLaunchState(launch.state)
      )
        ? LIVE_POLL_MS
        : false,
  });

export const useHpcAppLaunch = (
  launchId?: string
): UseQueryResult<HpcAppLaunchView, Error> =>
  useQuery<HpcAppLaunchView, Error>({
    queryKey: [...hpcAppLaunchesQueryKey, launchId],
    queryFn: async () => {
      const res = await axios.get(`${HPC_APP_LAUNCHES_BASE}/${launchId}`);
      return res.data as HpcAppLaunchView;
    },
    enabled: Boolean(launchId),
    retry: false,
    refetchInterval: query =>
      query.state.data && isLiveAppLaunchState(query.state.data.state)
        ? LIVE_POLL_MS
        : false,
  });

export const useCreateHpcAppLaunch = (): UseMutationResult<
  HpcAppLaunchView,
  unknown,
  CreateHpcAppLaunchBody
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateHpcAppLaunchBody) => {
      const res = await axios.post(HPC_APP_LAUNCHES_BASE, body);
      return res.data as HpcAppLaunchView;
    },
    onSuccess: (): void => {
      queryClient.invalidateQueries({ queryKey: hpcAppLaunchesQueryKey });
    },
  });
};

export const useStopHpcAppLaunch = (): UseMutationResult<
  HpcAppLaunchView,
  unknown,
  string
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (launchId: string) => {
      // An empty JSON body: with no body axios sends no content type and the server answers 415.
      const res = await axios.post(
        `${HPC_APP_LAUNCHES_BASE}/${launchId}/stop`,
        {}
      );
      return res.data as HpcAppLaunchView;
    },
    onSuccess: (): void => {
      queryClient.invalidateQueries({ queryKey: hpcAppLaunchesQueryKey });
    },
  });
};

/** The URL is single use and good for 60 seconds, so it is opened at once and never stored. */
export const useOpenHpcAppLaunch = (): UseMutationResult<
  OpenHpcAppLaunchResponse,
  unknown,
  string
> =>
  useMutation({
    mutationFn: async (launchId: string) => {
      // An empty JSON body, as for stop: without one the server answers 415.
      const res = await axios.post(
        `${HPC_APP_LAUNCHES_BASE}/${launchId}/open`,
        {}
      );
      return res.data as OpenHpcAppLaunchResponse;
    },
  });

export const useStartAppsProbe = (
  environmentId?: number
): UseMutationResult<StartRuntimeProbeResponseBody, unknown, void> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await axios.post(
        `${RUN_ENVIRONMENT_BASE}/${environmentId}/container-runtime/probe`,
        { kind: "apps" }
      );
      return res.data as StartRuntimeProbeResponseBody;
    },
    onSettled: (): void => {
      queryClient.invalidateQueries({
        queryKey: containerRuntimeQueryKey(environmentId),
      });
    },
  });
};

/** One agent environment a launch may target, with what its container runtime GET returned. */
export interface AppLaunchEnvironment {
  environment: ProfileClusterForClient;
  settings: RunEnvironmentContainerRuntimeResponseBody;
}

/** Apps on, the app check passed, and it reached the node through the agent relay. */
export const passedAppCheckWithRelay = (
  settings: RunEnvironmentContainerRuntimeResponseBody
): boolean =>
  Boolean(
    settings.apps?.enabled &&
    settings.apps.probe.state === "passed" &&
    settings.apps.networkMode === "relay" &&
    settings.appCaps
  );

/**
 * The user's agent environments that may take a launch: each one whose container runtime GET
 * succeeds (its owner, an admin of its organization, a platform admin) and whose app check passed
 * with `relay`. A GET answering 403 or 404 drops that environment quietly; a borrower's grant is
 * refused at launch anyway (`apps_not_allowed_on_grant`).
 */
export const useAppLaunchEnvironments = (
  enabled: boolean
): { environments: AppLaunchEnvironment[]; isLoading: boolean } => {
  // The run view: every environment the session user can run on (their own and the group shared
  // ones), never an impersonated user's, because a launch always runs as the signed in user. One
  // they cannot configure answers the runtime GET with 403 and drops out below. Not fetched at all
  // while the action does not apply.
  const { data: saved, isLoading: isSavedLoading } = useQuery<
    GetUserCredentialsResponse,
    Error
  >({
    queryKey: ["hpc-app-launch-environments"],
    queryFn: async () => {
      const res = await axios.get(USER_ENVIRONMENTS_PATH, {
        params: { type: CredentialView.Run },
      });
      return res.data as GetUserCredentialsResponse;
    },
    enabled,
    retry: false,
  });
  const agentEnvironments = enabled
    ? (saved?.profiles ?? []).filter(profile => "agent" === profile.transport)
    : [];
  const results = useQueries({
    queries: agentEnvironments.map(environment => ({
      queryKey: containerRuntimeQueryKey(environment.id),
      queryFn: () => fetchContainerRuntime(environment.id),
      retry: false,
    })),
  });
  const environments = agentEnvironments.flatMap((environment, index) => {
    const settings = results[index]?.data;
    return settings && passedAppCheckWithRelay(settings)
      ? [{ environment, settings }]
      : [];
  });
  return {
    environments,
    isLoading:
      enabled && (isSavedLoading || results.some(result => result.isLoading)),
  };
};

type ErrorResponse = {
  response?: {
    status?: number;
    data?: { reason?: string; detail?: string; message?: string };
  };
};

export const statusOf = (error: unknown): number | undefined =>
  (error as ErrorResponse)?.response?.status;
export const reasonOf = (error: unknown): string | undefined =>
  (error as ErrorResponse)?.response?.data?.reason;
export const detailOf = (error: unknown): string | undefined =>
  (error as ErrorResponse)?.response?.data?.detail ??
  (error as ErrorResponse)?.response?.data?.message;

export const NEEDS_AGENT_ENVIRONMENT = "HPC apps need an agent run environment";
export const AGENT_NOT_CONNECTED = "Start your agent on this environment";
export const AGENT_TOO_OLD =
  "Update the agent on this environment to 0.7.0 or later";
export const APPS_PROBE_OWNER_ONLY =
  "Only the environment owner can run the app check";

/** Refusal reasons the screens put in words. Any other reason shows the server's own sentence. */
export const REFUSAL_TEXT: Readonly<Record<string, string>> = {
  agent_not_connected: AGENT_NOT_CONNECTED,
  agent_too_old: AGENT_TOO_OLD,
  apps_need_agent_environment: NEEDS_AGENT_ENVIRONMENT,
  apps_probe_owner_only: APPS_PROBE_OWNER_ONLY,
  apps_not_allowed_on_grant:
    "Apps cannot be launched on a run environment you use through a grant",
  app_kind_not_supported: "This kind of app cannot run on a cluster yet",
  apps_unreachable:
    "The app check could not reach a compute node through the agent",
  hpc_apps_disabled: "HPC apps are not enabled on this server",
};

/** A refusal in words: the known reasons first, then the server's detail, then `fallback`. */
export const refusalText = (error: unknown, fallback: string): string => {
  const reason = reasonOf(error);
  if (reason && REFUSAL_TEXT[reason]) return REFUSAL_TEXT[reason];
  return detailOf(error) ?? fallback;
};

export const END_REASON_TEXT: Readonly<Record<AppLaunchEndReason, string>> = {
  user_stopped: "You stopped it.",
  idle: "Stopped after its idle limit passed with no activity.",
  wall_time: "Reached its walltime.",
  failed_to_start: "The app did not start.",
  lost: "The cluster lost the job.",
  admin_stopped: "An administrator stopped it.",
  dispatch_refused: "The cluster refused the job.",
};
